import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import type { ApiResponse, ServiceGroup } from '../types/index';

export interface ServiceUsageStatus {
  serviceGroup: ServiceGroup;
  source: 'subscription' | 'legacy';
  planName: string;
  unit: 'runs' | 'words';
  limit: number | null;
  usedToday: number;
  /** Whether Pay-per-Run is even offered for this service — false for
   *  Humanizer, which has no Pay-per-Run fallback at all. */
  pprEligible: boolean;
  /** null when !pprEligible (Humanizer). */
  bonusRunsAvailable: number | null;
  limitReached: boolean;
  expiresAt: string | null;
}

export const SERVICE_USAGE_QUERY_KEY = ['service-usage'] as const;

/** Today's usage for one service group — subscription-scoped if the caller
 *  has an active Limited/Pro subscription covering it, else the same
 *  Free/Pay-per-Run status enforceServiceAccess checks server-side. */
export function useServiceUsage(serviceGroup: ServiceGroup) {
  const { data, isLoading, isError } = useQuery({
    queryKey: [...SERVICE_USAGE_QUERY_KEY, serviceGroup],
    queryFn: () =>
      api
        .get<ApiResponse<ServiceUsageStatus>>(`/ai-runs/usage-summary?serviceGroup=${serviceGroup}`)
        .then((r) => r.data.data ?? null),
    staleTime: 15_000,
  });

  return { usage: data ?? null, isLoading, isError };
}
